import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";

// =============================================================================
// useDialProof — data layer for the Dial Analytics proof drill-down modal
// =============================================================================
// Two server functions, one dispatch helper underneath (dial_proof_rows):
//   get_dial_proof       → the bucket histogram for a metric (the modal's bars)
//   get_dial_proof_rows  → one bucket's paginated proof rows (the modal's list)
// A card and its modal read the SAME row definition on the server, so they
// reconcile by construction. Types are co-located here, matching the sibling
// useDialerConnects convention.
// =============================================================================

// The 12 drill-down metrics. Every clickable dial card (and every Dial Mix
// matrix cell, via "mix_cell") maps to exactly one.
export type DialProofMetric =
  | "dials"
  | "mix_cell"
  | "real_connects"
  | "conversations"
  | "talk_time"
  | "status_mix"
  | "hourly"
  | "attempts_per_lead"
  | "booked"
  | "speed_to_lead"
  | "recordings"
  | "active_days";

export type DialCountMode = "attempts" | "raw";

export interface DialProofBucket {
  bucket: string;
  count: number;
  durationSumSec: number;
}

// One word of a transcript with its playback position. The click-to-seek unit.
// The transcription worker (scripts/transcribe_dial_backlog.py --word-times)
// normalizes whisper's -oj output into this shape BEFORE storage, so the column
// holds a clean array, not raw whisper json.
export interface WordTime {
  word: string;
  start_ms: number;
  end_ms: number;
}

// Exactly 18 fields — the server strips total_count off each row.
// transcriptJson is NULL until the prod dial_dials.transcript_json column lands
// (Mako's push) + get_dial_proof_rows selects it. Absent => flat-transcript
// fallback in the modal, so shipping this ahead of the column is a no-op in prod.
export interface DialProofRow {
  dialId: string;
  leadId: string | null;
  conversationId: string | null;
  leadName: string | null;
  leadPhone: string | null;
  dialerId: string | null;
  dialerName: string | null;
  startedAt: string | null;
  durationSec: number | null;
  status: string | null;
  direction: string | null;
  bucket: string | null;
  recordingUrl: string | null;
  hasTranscript: boolean;
  transcript: string | null;
  transcriptJson: WordTime[] | null;
  funnelAtDial: string | null;
  intentAtDial: string | null;
}

export interface DialProofRowsPage {
  total: number;
  rows: DialProofRow[];
  asOf: string | null;
}

// ── Pure parsers (node-safe — the vitest suite exercises these, no DOM) ──────

/* eslint-disable @typescript-eslint/no-explicit-any */
export function parseHistogram(
  raw: any
): { dialerId: string | null; buckets: DialProofBucket[] }[] {
  const dialers = raw?.dialers ?? [];
  return dialers.map((d: any) => ({
    dialerId: d?.dialer_id ?? null,
    buckets: (d?.buckets ?? []).map((b: any) => ({
      bucket: String(b?.bucket ?? ""),
      count: Number(b?.count ?? 0),
      durationSumSec: Number(b?.duration_sum_sec ?? 0),
    })),
  }));
}

// Boundary guard for the stored word-time array. The worker already normalizes,
// so this just defends against junk: non-array => null (fallback path); rows with
// a missing word or unparseable start are dropped; a missing end clamps to start.
// Empty result => null so the modal treats "column present but empty" as fallback.
export function parseWordTimes(raw: any): WordTime[] | null {
  if (!Array.isArray(raw)) return null;
  const out: WordTime[] = [];
  for (const w of raw) {
    if (!w || typeof w !== "object") continue;
    const word = typeof w.word === "string" ? w.word : "";
    const start = Number(w.start_ms);
    if (!word || !Number.isFinite(start)) continue;
    const end = Number(w.end_ms);
    out.push({ word, start_ms: start, end_ms: Number.isFinite(end) ? end : start });
  }
  return out.length ? out : null;
}

export function parseRow(r: any): DialProofRow {
  return {
    dialId: r?.dial_id,
    leadId: r?.lead_id ?? null,
    conversationId: r?.conversation_id ?? null,
    leadName: r?.lead_name ?? null,
    leadPhone: r?.lead_phone ?? null,
    dialerId: r?.dialer_id ?? null,
    dialerName: r?.dialer_name ?? null,
    startedAt: r?.started_at ?? null,
    durationSec: r?.duration_sec ?? null,
    status: r?.status ?? null,
    direction: r?.direction ?? null,
    bucket: r?.bucket ?? null,
    recordingUrl: r?.recording_url ?? null,
    hasTranscript: !!r?.has_transcript,
    transcript: r?.transcript ?? null,
    transcriptJson: parseWordTimes(r?.transcript_json),
    funnelAtDial: r?.funnel_at_dial ?? null,
    intentAtDial: r?.intent_at_dial ?? null,
  };
}

export function parseRowsPage(raw: any): DialProofRowsPage {
  return {
    total: Number(raw?.total ?? 0),
    asOf: raw?.as_of ?? null,
    rows: (raw?.rows ?? []).map(parseRow),
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ── Row fetcher (imperative — the modal calls it on bucket-select + paging) ──
// p_as_of is the pagination snapshot: captured once when the modal opens and
// passed unchanged on every page, so the 2-minute sync cron can't re-serve rows.
export async function fetchDialProofRows(params: {
  organizationId: string;
  dialerId: string | null;
  metric: DialProofMetric;
  bucket: string;
  start: Date | null;
  end: Date | null;
  limit: number;
  offset: number;
  asOf: string | null;
  countMode: DialCountMode;
  /** mix_cell drill-down: pin the funnel × intent cell. NULL for every other metric. */
  funnel?: string | null;
  intent?: string | null;
}): Promise<DialProofRowsPage> {
  if (!supabase) return { total: 0, rows: [], asOf: params.asOf };
  const end = params.end ? new Date(params.end) : null;
  if (end) end.setHours(23, 59, 59, 999);
  const { data, error } = await supabase.rpc("get_dial_proof_rows", {
    p_organization_id: params.organizationId,
    p_dialer_id: params.dialerId,
    p_start_date: params.start?.toISOString() ?? null,
    p_end_date: end?.toISOString() ?? null,
    p_metric: params.metric,
    p_bucket: params.bucket,
    p_limit: params.limit,
    p_offset: params.offset,
    p_funnel: params.funnel ?? null,
    p_intent: params.intent ?? null,
    p_count_mode: params.countMode,
    p_as_of: params.asOf,
  });
  if (error) {
    logger.error("get_dial_proof_rows error:", error);
    throw error;
  }
  return parseRowsPage(data);
}

// ── Histogram hook (drives the modal's bar chart) ────────────────────────────
export function useDialProofHistogram(
  organizationId: string | null,
  metric: DialProofMetric | null,
  dialerId: string | null,
  dateRange: { start: Date | null; end: Date | null },
  countMode: DialCountMode = "attempts",
  // mix_cell drill-down: pin the funnel × intent cell. NULL for every other metric.
  funnel: string | null = null,
  intent: string | null = null
) {
  const [buckets, setBuckets] = useState<DialProofBucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    if (!supabase || !organizationId || !metric) {
      setBuckets([]);
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const controller = abortRef.current;
    setLoading(true);
    setError(null);

    const end = dateRange.end ? new Date(dateRange.end) : null;
    if (end) end.setHours(23, 59, 59, 999);

    try {
      const { data, error: rpcError } = await supabase
        .rpc("get_dial_proof", {
          p_organization_id: organizationId,
          p_dialer_ids: dialerId ? [dialerId] : null,
          p_start_date: dateRange.start?.toISOString() ?? null,
          p_end_date: end?.toISOString() ?? null,
          p_metric: metric,
          p_funnel: funnel,
          p_intent: intent,
          p_count_mode: countMode,
        })
        .abortSignal(controller.signal);
      if (rpcError) {
        if (controller.signal.aborted) return;
        logger.error("get_dial_proof error:", rpcError);
        setError(rpcError.message);
        return;
      }
      const parsed = parseHistogram(data);
      // Org-wide (dialerId null) or single dialer → one entry; flatten to its buckets.
      setBuckets(parsed[0]?.buckets ?? []);
    } catch (err) {
      if (controller.signal.aborted) return;
      if ((err as Error).name === "AbortError") return;
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [organizationId, metric, dialerId, dateRange.start, dateRange.end, countMode, funnel, intent]);

  useEffect(() => {
    fetchData();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchData]);

  return { buckets, loading, error };
}
